// The Squad's live adapter (Track 2, T2-10b): app-api → the same ProtoSquadPlayer rows the
// snapshot gives the widget. Teams by id (B1), on ?live=1 only — the snapshot has no team ids.
//
// - The teams: the league's newest table (`loadLeagueTable` — the newest season with a played
//   match, as League Table opens on), as the snapshot took them from its newest league table.
// - The squad: `/squad` (who's in it: "current team", T2-10a) + one `POST /player-stats` for the
//   whole club (minutes per player per position) → each player's position, the same rule as
//   Bio (`mainPositionGroup`).
// - The sticky player's club: `/player-current-league`.

import { rememberFamiliarName } from "../data/familiar-names.js";
import { mainPositionGroup } from "@catbento/shared";
import type {
  PlayerCurrentLeague,
  PlayerStatsRequest,
  PlayerStatsResponse,
  PlayerStatsRow,
  ProtoSquadPlayer,
  SquadResponse,
} from "@catbento/shared";
import type { CompetitionId, LeagueChoice } from "../data/league.js";
import type { PlayerId } from "../data/players.js";
import { comparePlayersByName, getPlayerShortName } from "../data/player-utils.js";
import type { LiveApi } from "./api.js";
import { loadLeagueTable } from "./league.js";

export interface SquadTeam {
  id: number;
  name: string;
}

/** A league's clubs, and the season their squads are shown for (null = no played match). */
export interface LiveSquadTeams {
  seasonId: number | null;
  /** Sorted by name, as the snapshot's picker. */
  teams: SquadTeam[];
}

export async function loadSquadTeams(api: LiveApi, league: LeagueChoice): Promise<LiveSquadTeams> {
  const table = await loadLeagueTable(api, league);
  if (!table.table.length) return { seasonId: null, teams: [] };
  const teams = table.table.map((row) => {
    if (row.teamId === undefined) throw new Error(`/league-table row "${row.name}" has no teamId`);
    return { id: row.teamId, name: row.name };
  });
  teams.sort((a, b) => a.name.localeCompare(b.name));
  return { seasonId: Number(table.seasonCode), teams };
}

/**
 * The stored team → its key (String(id)) in this league's list. A number is an id (live). A
 * string is a club NAME — the snapshot's way, e.g. a saved dashboard — so it's found by
 * name. Null = not in this league (the widget then opens on the sticky club or the first).
 */
export function storedTeamKey(stored: unknown, teams: SquadTeam[]): string | null {
  const team =
    typeof stored === "number"
      ? teams.find((t) => t.id === stored)
      : typeof stored === "string"
        ? teams.find((t) => t.name === stored)
        : undefined;
  return team ? String(team.id) : null;
}

/** Minutes per player per position, for the whole club in one league season. */
export function squadMinutesRequest(teamId: number, competitionId: CompetitionId, seasonId: number): PlayerStatsRequest {
  return {
    keys: ["minutes"],
    where: { team_id: [teamId], competition_id: [competitionId], season_id: [seasonId] },
    groupBy: ["player_id", "position_id"],
  };
}

/** Each player's position template from the club's minutes-by-position rows (Bio's rule). A
 *  player with no rows (an unused sub) isn't in the map → "—". */
export function positionsByPlayer(rows: PlayerStatsRow[]): Map<number, string | null> {
  const byPlayer = new Map<number, PlayerStatsRow[]>();
  for (const row of rows) {
    const id = Number(row.player_id);
    if (!Number.isInteger(id)) throw new Error(`Minutes row without a player id: ${JSON.stringify(row)}`);
    const mine = byPlayer.get(id);
    if (mine) mine.push(row);
    else byPlayer.set(id, [row]);
  }
  return new Map([...byPlayer].map(([id, mine]) => [id, mainPositionGroup(mine)]));
}

export async function loadSquad(
  api: LiveApi,
  teamId: number,
  competitionId: CompetitionId,
  seasonId: number,
): Promise<ProtoSquadPlayer[]> {
  const [{ players }, { rows }] = await Promise.all([
    api.get<SquadResponse>(`/squad?teamId=${teamId}&competitionId=${competitionId}&seasonId=${seasonId}`),
    api.post<PlayerStatsResponse>("/player-stats", squadMinutesRequest(teamId, competitionId, seasonId)),
  ]);
  const positions = positionsByPlayer(rows);
  // Each player's familiar name first ("Lionel Messi"), so the rows and their order use it.
  for (const p of players) rememberFamiliarName(p.name, p.nickname);
  return [...players].sort(comparePlayersByName).map((p) => ({
    playerId: p.id,
    name: p.name,
    shortName: getPlayerShortName(p),
    positionTemplate: positions.get(p.id) ?? null,
    country: p.country,
    dob: p.dob,
    hopsRating: p.hops,
  }));
}

/** The sticky player's club (her current domestic league + team), or nulls when she has none. */
export async function loadStickyClub(
  api: LiveApi,
  playerId: PlayerId,
): Promise<Pick<PlayerCurrentLeague, "competitionId" | "teamId" | "teamName">> {
  const { competitionId, teamId, teamName } = await api.get<PlayerCurrentLeague>(`/player-current-league?playerId=${playerId}`);
  return { competitionId, teamId, teamName };
}
